import type { Tracking } from './index';
import {
  initTracking as coreInitTracking,
  requireTracker,
  isTrackerReady,
  getTrackerInfo,
  isUsingMockTracker,
  canRetryInit,
  resetForRetry,
  getCoreDiagnostics,
} from './core';
import { isDebugSnowplowDisabled } from './debugState';
import { getCustomIdContext } from './customIdContext';
import { shouldTrackEvent } from '@/utils/eventDeduplication';
import { isPageViewSuppressed } from '@/utils/pageViewSuppression';

const __DEV_MODE__ = process.env.NODE_ENV !== 'production';
const DEFAULT_WEB_APP_ID = __DEV_MODE__ ? 'test_dv_ww_hekaya' : 'prod_dv_ww_hekaya';

const GEOIP_TIMEOUT_MS = 2000;
const COLLECTOR_HEALTHCHECK_TIMEOUT_MS = 3000;
const MAX_QUEUED_EVENTS_PER_TYPE = 60;
// Queued events must survive the whole consent wait: Didomi script deferral,
// 3s notice delay, then the user's decision — which routinely takes minutes.
const EVENT_TTL_MS = 600_000;
const RETRY_BACKOFF_BASE_MS = 2000;
const MAX_RETRY_ATTEMPTS = 3;

enum TrackerState {
  NOT_INITIALIZED = 'NOT_INITIALIZED',
  INITIALIZING = 'INITIALIZING',
  READY = 'READY',
  FAILED_RETRYABLE = 'FAILED_RETRYABLE',
  FAILED_PERMANENT = 'FAILED_PERMANENT',
  NO_COLLECTOR = 'NO_COLLECTOR',
}

let trackerState: TrackerState = TrackerState.NOT_INITIALIZED;
let initializationPromise: Promise<void> | null = null;
let cachedCountryCode: string | null = null;
let retryAttemptCount = 0;
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
let lastInitAttemptTimestamp: number | null = null;

interface TimestampedEvent {
  type: 'pageView' | 'dvLogin' | 'setUserId';
  args: any[];
  queuedAt: number;
  // custom_id context captured at fire time, so replayed events keep the
  // snp_csid they were fired with (a DCB callback or a logout can change the
  // live context while events sit in the queue).
  customIdCtx?: { schema: string; data: unknown } | null;
}

const eventQueues: Record<string, TimestampedEvent[]> = {
  pageView: [],
  dvLogin: [],
  setUserId: [],
};

function getQueuedEventCount(): number {
  return Object.values(eventQueues).reduce((sum, q) => sum + q.length, 0);
}

function enqueueEvent(event: TimestampedEvent): boolean {
  const queue = eventQueues[event.type];
  if (!queue) return false;

  if (event.type !== 'setUserId') {
    event.customIdCtx = getCustomIdContext();
  }

  if (queue.length >= MAX_QUEUED_EVENTS_PER_TYPE) {
    console.warn(
      `[snowplow][web] Queue overflow for "${event.type}": ${queue.length}/${MAX_QUEUED_EVENTS_PER_TYPE} events. ` +
      `Dropping oldest event to make room.`
    );
    queue.shift();
  }

  queue.push(event);
  return true;
}

function pruneExpiredEvents(): number {
  const now = Date.now();
  let pruned = 0;
  for (const type of Object.keys(eventQueues)) {
    const before = eventQueues[type].length;
    eventQueues[type] = eventQueues[type].filter(e => (now - e.queuedAt) < EVENT_TTL_MS);
    pruned += before - eventQueues[type].length;
  }
  if (pruned > 0) {
    console.warn(`[snowplow][web] Pruned ${pruned} expired event(s) from queue (TTL: ${EVENT_TTL_MS}ms)`);
  }
  return pruned;
}

function isEffectivelyReady(): boolean {
  return trackerState === TrackerState.READY && isTrackerReady() && !isUsingMockTracker();
}

function shouldQueueEvents(): boolean {
  return (
    trackerState === TrackerState.NOT_INITIALIZED ||
    trackerState === TrackerState.INITIALIZING ||
    trackerState === TrackerState.FAILED_RETRYABLE
  );
}

async function fetchGeoIPCountryCode(): Promise<string | null> {
  const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('[snowplow][web] GeoIP fallback: missing Supabase env vars');
    return null;
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), GEOIP_TIMEOUT_MS);

    const response = await fetch(`${supabaseUrl}/functions/v1/geoip`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (!response.ok) {
      console.warn(`[snowplow][web] GeoIP fallback: HTTP ${response.status}`);
      return null;
    }

    const json = await response.json();
    if (json.error === 0 && json.data?.success) {
      const code = json.data.success as string;
      console.log(`[snowplow][web] GeoIP fallback: resolved country code: ${code}`);
      return code;
    }

    console.warn('[snowplow][web] GeoIP fallback: unexpected response', json);
    return null;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.warn(`[snowplow][web] GeoIP fallback failed: ${msg}`);
    return null;
  }
}

async function checkCollectorHealth(collectorUrl: string): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), COLLECTOR_HEALTHCHECK_TIMEOUT_MS);

    const response = await fetch(collectorUrl, {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
    });
    clearTimeout(timeout);

    console.log(`[snowplow][web] Collector health-check passed (${collectorUrl})`);
    return true;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.warn(`[snowplow][web] Collector health-check FAILED for ${collectorUrl}: ${msg}`);
    console.warn('[snowplow][web] Events may not reach the collector (CSP/CORS/network issue)');
    return false;
  }
}

function sendDvLogin(
  payload: import('./index').DvLoginEvent,
  contextEntities?: Array<{ schema: string; data: unknown }>,
  customIdCtx?: { schema: string; data: unknown } | null
): void {
  const tracker = requireTracker();
  const eventData = {
    schema: 'iglu:com.dgp/dv_login/jsonschema/1-0-6',
    data: payload
  };

  const contexts = [
    ...(Array.isArray(contextEntities) ? contextEntities : []),
    ...(customIdCtx ? [customIdCtx] : []),
  ];

  if (contexts.length > 0) {
    tracker.trackSelfDescribingEvent(eventData, contexts);
  } else {
    tracker.trackSelfDescribingEvent(eventData);
  }
}

function sendPageView(
  pageName: string,
  pageUrl?: string,
  customIdCtx?: { schema: string; data: unknown } | null
): void {
  const tracker = requireTracker();
  const contexts = customIdCtx ? [customIdCtx] : undefined;
  if (typeof tracker.trackPageView === 'function') {
    // Built-in Snowplow page_view event (event_name "page_view" in the
    // warehouse, page_url captured from the location). An
    // iglu:...snowplow/page_view self-describing schema does not exist —
    // sending one fails enrichment and lands the event in the bad index.
    tracker.trackPageView(pageName, contexts);
  } else {
    tracker.trackSelfDescribingEvent({
      schema: 'iglu:com.snowplowanalytics.snowplow/screen_view/jsonschema/1-0-0',
      data: {
        name: pageName,
        id: pageUrl || pageName
      }
    }, contexts);
  }
}

function flushEventQueue(): void {
  pruneExpiredEvents();
  const totalQueued = getQueuedEventCount();
  if (totalQueued === 0) return;

  console.log(`[snowplow][web] Flushing ${totalQueued} queued event(s)`);

  for (const type of ['setUserId', 'dvLogin', 'pageView']) {
    const events = eventQueues[type].splice(0);
    for (const event of events) {
      try {
        switch (event.type) {
          case 'pageView':
            sendPageView(event.args[0], event.args[1], event.customIdCtx);
            break;
          case 'dvLogin':
            sendDvLogin(event.args[0], event.args[1], event.customIdCtx);
            break;
          case 'setUserId':
            setUserId(event.args[0]);
            break;
        }
      } catch (err) {
        console.warn(`[snowplow][web] Error replaying queued ${event.type} event:`, err);
      }
    }
  }
}

function scheduleRetry(): void {
  if (retryTimeoutId) return;
  if (retryAttemptCount >= MAX_RETRY_ATTEMPTS || !canRetryInit()) {
    trackerState = TrackerState.FAILED_PERMANENT;
    console.error(`[snowplow][web] All retry attempts exhausted (${retryAttemptCount}/${MAX_RETRY_ATTEMPTS}). Pipeline permanently disabled.`);
    return;
  }

  const delay = RETRY_BACKOFF_BASE_MS * Math.pow(2, retryAttemptCount);
  console.log(`[snowplow][web] Scheduling retry #${retryAttemptCount + 1} in ${delay}ms`);

  retryTimeoutId = setTimeout(() => {
    retryTimeoutId = null;
    retryAttemptCount++;
    resetForRetry();
    trackerState = TrackerState.NOT_INITIALIZED;
    initializationPromise = null;

    initTracking(cachedCountryCode).catch((error) => {
      console.error('[snowplow][web] Retry initialization failed:', error);
    });
  }, delay);
}

export async function initTracking(countryCode?: string | null): Promise<void> {
  if (countryCode) {
    cachedCountryCode = countryCode;
  }

  if (trackerState === TrackerState.READY && isEffectivelyReady()) {
    const trackerInfo = getTrackerInfo();
    if (countryCode && trackerInfo.countryCode !== countryCode) {
      console.log(`[snowplow][web] Re-initializing tracker: country changed from ${trackerInfo.countryCode || 'NOT_SET'} to ${countryCode}`);
      trackerState = TrackerState.NOT_INITIALIZED;
      initializationPromise = null;
      retryAttemptCount = 0;
    } else {
      return;
    }
  }

  if (trackerState === TrackerState.READY && isUsingMockTracker() && canRetryInit()) {
    console.log('[snowplow][web] Currently using mock tracker, attempting real initialization');
    resetForRetry();
    trackerState = TrackerState.NOT_INITIALIZED;
    initializationPromise = null;
  }

  if (trackerState === TrackerState.FAILED_PERMANENT || trackerState === TrackerState.NO_COLLECTOR) {
    return;
  }

  if (initializationPromise && trackerState === TrackerState.INITIALIZING) {
    return initializationPromise;
  }

  trackerState = TrackerState.INITIALIZING;
  lastInitAttemptTimestamp = Date.now();

  initializationPromise = (async () => {
    try {
      const namespace = process.env.EXPO_PUBLIC_SNOWPLOW_NAMESPACE || 'web';
      const collector = process.env.EXPO_PUBLIC_SNOWPLOW_COLLECTOR_URL;
      const appId = process.env.EXPO_PUBLIC_SNOWPLOW_APP_ID || DEFAULT_WEB_APP_ID;

      if (!collector) {
        console.warn('[snowplow][web] No collector URL configured. Snowplow pipeline is disabled.');
        trackerState = TrackerState.NO_COLLECTOR;
        return;
      }

      let effectiveCountryCode = cachedCountryCode;

      if (!effectiveCountryCode) {
        effectiveCountryCode = await fetchGeoIPCountryCode();
        if (effectiveCountryCode) {
          cachedCountryCode = effectiveCountryCode;
        }
      }

      console.log('[snowplow][web] Initializing tracker:', {
        ns: namespace,
        collector,
        appId,
        countryCode: effectiveCountryCode || 'NOT_SET',
        attempt: retryAttemptCount + 1,
      });

      await checkCollectorHealth(collector);

      const forceReinitialize = isTrackerReady();
      await coreInitTracking({
        endpoint: collector,
        namespace,
        appId,
        countryCode: effectiveCountryCode,
        forceReinitialize,
      });

      if (isUsingMockTracker()) {
        console.warn('[snowplow][web] Core returned mock tracker');
        trackerState = TrackerState.FAILED_RETRYABLE;
        scheduleRetry();
        return;
      }

      trackerState = TrackerState.READY;
      retryAttemptCount = 0;
      console.log(`[snowplow][web] Tracker initialized successfully (countryCode: ${effectiveCountryCode || 'NOT_SET'})`);

      flushEventQueue();
    } catch (error) {
      console.error('[snowplow][web] Tracker initialization failed:', error);
      trackerState = TrackerState.FAILED_RETRYABLE;
      initializationPromise = null;
      scheduleRetry();
    }
  })();

  return initializationPromise;
}

export async function setUserId(id: string | null): Promise<void> {
  if (isDebugSnowplowDisabled()) return;

  if (shouldQueueEvents()) {
    enqueueEvent({ type: 'setUserId', args: [id], queuedAt: Date.now() });
    return;
  }

  if (!isEffectivelyReady()) {
    console.warn('[snowplow][web] Tracker not effectively ready, queuing setUserId');
    enqueueEvent({ type: 'setUserId', args: [id], queuedAt: Date.now() });
    return;
  }

  const tracker = requireTracker();
  tracker.setUserId(id ?? null);
}

export async function trackDvLogin(
  payload: import('./index').DvLoginEvent,
  contextEntities?: Array<{ schema: string; data: unknown }>
): Promise<void> {
  if (isDebugSnowplowDisabled()) return;

  if (shouldQueueEvents()) {
    console.log(`[snowplow][web] Queuing dvLogin event (state: ${trackerState})`);
    enqueueEvent({ type: 'dvLogin', args: [payload, contextEntities], queuedAt: Date.now() });
    return;
  }

  if (!isEffectivelyReady()) {
    console.warn(`[snowplow][web] Tracker not effectively ready (state: ${trackerState}, mock: ${isUsingMockTracker()}), queuing dvLogin`);
    enqueueEvent({ type: 'dvLogin', args: [payload, contextEntities], queuedAt: Date.now() });
    return;
  }

  try {
    // No snapshot on the live path: the wrapper injects the current custom_id
    // context at dispatch time.
    sendDvLogin(payload, contextEntities);
  } catch (error) {
    console.error('[snowplow][web] Error sending dv_login event:', error);
  }
}

export async function trackPageView(pageName: string, pageUrl?: string): Promise<void> {
  if (isDebugSnowplowDisabled()) return;
  // Focus artifact of the player opening, not a real navigation.
  if (isPageViewSuppressed()) {
    console.log(`[snowplow][web] page_view "${pageName}" skipped (player launching)`);
    return;
  }
  if (!shouldTrackEvent('page_view', pageName, pageUrl)) return;

  if (shouldQueueEvents()) {
    enqueueEvent({ type: 'pageView', args: [pageName, pageUrl], queuedAt: Date.now() });
    return;
  }

  if (!isEffectivelyReady()) {
    enqueueEvent({ type: 'pageView', args: [pageName, pageUrl], queuedAt: Date.now() });
    return;
  }

  try {
    sendPageView(pageName, pageUrl);
  } catch (error) {
    console.error('[snowplow][web] Error tracking page view:', error);
  }
}

export function getSnowplowWebDiagnostics(): {
  trackerState: string;
  isEffectivelyReady: boolean;
  queuedEvents: Record<string, number>;
  totalQueued: number;
  cachedCountryCode: string | null;
  retryAttemptCount: number;
  maxRetries: number;
  lastInitAttempt: string | null;
  retryScheduled: boolean;
  core: ReturnType<typeof getCoreDiagnostics>;
} {
  return {
    trackerState,
    isEffectivelyReady: isEffectivelyReady(),
    queuedEvents: {
      pageView: eventQueues.pageView.length,
      dvLogin: eventQueues.dvLogin.length,
      setUserId: eventQueues.setUserId.length,
    },
    totalQueued: getQueuedEventCount(),
    cachedCountryCode,
    retryAttemptCount,
    maxRetries: MAX_RETRY_ATTEMPTS,
    lastInitAttempt: lastInitAttemptTimestamp ? new Date(lastInitAttemptTimestamp).toISOString() : null,
    retryScheduled: retryTimeoutId !== null,
    core: getCoreDiagnostics(),
  };
}

export const tracking: Tracking = { initTracking, trackDvLogin, setUserId, trackPageView };
